import onetime from "@-/com.npmjs.onetime";
import { View } from "@-/com.npmjs.react-native";
import {
  runOnJS,
  useFrameCallback,
  useReducedMotion,
  useSharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import { useCSSVariable } from "@-/com.npmjs.uniwind";
import { useAsyncComponent } from "@-/dev.improve.haniverse.lib.use-async-component";
import {
  isWelcomeGalaxyDive,
  registerWelcomeGalaxyEntryHost,
  welcomeGalaxyDiveRevealStart,
  welcomeGalaxyDiveTarget,
  welcomeGalaxyEntryDestination,
  type WelcomeGalaxyScene,
} from "@-/dev.improve.haniverse.routes.nav.app.auth.welcome-galaxy-entry";
import { createElement, useCallback, useEffect, useMemo, useState } from "react";
import { isWelcomeGalaxySkiaDisabled } from "#is-welcome-galaxy-skia-disabled";
import { getWelcomeGalaxyMinStepSeconds } from "#use-welcome-galaxy-clock";
import { loadWelcomeGalaxyCanvas } from "#load-welcome-galaxy-canvas";
import type { WelcomeGalaxyEntryColors } from "#use-welcome-galaxy-frame";
import { welcomeGalaxyPalette } from "#welcome-galaxy-palette";
import { welcomeGalaxyThemeColor } from "#welcome-galaxy-theme-color";
import {
  WELCOME_GALAXY_DIVE_MAX_DURATION,
  WELCOME_GALAXY_DIVE_REVEAL_DURATION,
  WELCOME_GALAXY_ENTRY_DURATION,
  WELCOME_GALAXY_ENTRY_HANDOFF_T,
  computeWelcomeGalaxyDiveRevealStart,
} from "#welcome-galaxy-timeline";

/** Longest frame step, so a hitch never jumps the entry. */
const MAX_FRAME_SECONDS = 0.05;
/**
 * How far past its moment (ms) the navigation and the overlay's removal wait for the frame clock
 * before happening anyway: frames stop while the app is in the background, and neither may hang.
 */
const ENTRY_DEADLINE_SLACK_MS = 1000;

type WelcomeGalaxyEntry = { id: number; navigate: () => void; scene: WelcomeGalaxyScene };

/**
 * The Learn card's colours as the app's theme paints them right now, for the card the galaxy
 * becomes; the palette's own stand in for any token the theme cannot be read from.
 */
function useWelcomeGalaxyEntryColors(appearance: "dark" | "light"): WelcomeGalaxyEntryColors {
  const surface = welcomeGalaxyThemeColor(useCSSVariable("--color-surface"));
  const border = welcomeGalaxyThemeColor(useCSSVariable("--color-border"));
  return useMemo(() => {
    const fallback = welcomeGalaxyPalette(appearance).entryCard;
    return { border: border ?? fallback.border, surface: surface ?? fallback.surface };
  }, [appearance, border, surface]);
}

/**
 * Plays the entry into the Haniverse when someone leaves the welcome galaxy for the app (signing
 * in, signing up or continuing as a guest). The welcome page's headline and buttons fade while
 * its galaxy starts to zoom in; then this host's own canvas, drawn from the very same
 * shared values, takes over on top and the app is navigated to underneath it. The sky fades out
 * so the app's chrome appears around the card's viewport onto the galaxy. Characters stay sharp
 * inside the viewport and blur heavily outside it; then the canvas fades to the real card. A dive
 * into the core (a new learner's, or a sign-in's) instead dissolves, still diving, as soon as its
 * destination (the onboarding's Earth, or Learn's map) is on screen beneath it
 * (`welcomeGalaxyDiveTarget`), and lasts until it has.
 *
 * Mount once above the routes (`NavLayout`), so the entry outlives the welcome page. Under reduced
 * motion, or in builds without Skia, it does not register, and the app is navigated to directly.
 */
export function WelcomeGalaxyEntryHost() {
  const reduceMotion = useReducedMotion();
  const [entry, setEntry] = useState<WelcomeGalaxyEntry | null>(null);

  useEffect(() => {
    if (reduceMotion || isWelcomeGalaxySkiaDisabled()) {
      return;
    }
    return registerWelcomeGalaxyEntryHost(({ navigate, scene }) => {
      // Marks the entry as begun at once, which stops the welcome page's own clock.
      scene.entryTime.set(Number.EPSILON);
      setEntry((previous) => ({ id: (previous?.id ?? 0) + 1, navigate, scene }));
      return true;
    });
  }, [reduceMotion]);

  const onFinish = useCallback(() => setEntry(null), []);

  return entry === null ? null : (
    <WelcomeGalaxyEntryOverlay
      key={entry.id}
      navigate={entry.navigate}
      onFinish={onFinish}
      scene={entry.scene}
    />
  );
}

function WelcomeGalaxyEntryOverlay({
  navigate,
  onFinish,
  scene,
}: {
  navigate: () => void;
  onFinish: () => void;
  scene: WelcomeGalaxyScene;
}) {
  const Canvas = useAsyncComponent(loadWelcomeGalaxyCanvas);
  const { appearance, canvasCache, entryTime, launch, simTime, spinOffset, viewport } = scene;
  const entryColors = useWelcomeGalaxyEntryColors(appearance);
  const navigateOnce = useMemo(() => onetime(navigate), [navigate]);
  const finishOnce = useMemo(() => onetime(onFinish), [onFinish]);
  const destination = welcomeGalaxyEntryDestination();
  const diveRevealStart = welcomeGalaxyDiveRevealStart();
  const diveTarget = welcomeGalaxyDiveTarget();

  // Time since the entry last stepped, while frames are skipped (`getWelcomeGalaxyMinStepSeconds`).
  const pendingSeconds = useSharedValue(0);
  const minStepSeconds = getWelcomeGalaxyMinStepSeconds().active;

  const frameCallback = useFrameCallback((frame) => {
    "worklet";
    const frameSeconds = pendingSeconds.get() + (frame.timeSincePreviousFrame ?? 16) / 1000;
    if (frameSeconds < minStepSeconds) {
      pendingSeconds.set(frameSeconds);
      return;
    }
    pendingSeconds.set(0);
    const seconds = Math.min(frameSeconds, MAX_FRAME_SECONDS);
    const previous = entryTime.get();
    const next = previous + seconds;
    entryTime.set(next);
    simTime.set(simTime.get() + seconds);
    // The canvas is opaque from this very frame (`welcomeGalaxyEntryOverlayOpacity`), so the
    // route swap happens under it.
    if (previous < WELCOME_GALAXY_ENTRY_HANDOFF_T && next >= WELCOME_GALAXY_ENTRY_HANDOFF_T) {
      runOnJS(navigateOnce)();
    }
    let end = WELCOME_GALAXY_ENTRY_DURATION;
    if (isWelcomeGalaxyDive(destination.get())) {
      let revealStart = diveRevealStart.get();
      if (revealStart === null) {
        revealStart = computeWelcomeGalaxyDiveRevealStart(next, diveTarget.get());
        diveRevealStart.set(revealStart);
      }
      end =
        revealStart === null
          ? WELCOME_GALAXY_DIVE_MAX_DURATION
          : revealStart + WELCOME_GALAXY_DIVE_REVEAL_DURATION;
    }
    if (next >= end) {
      runOnJS(finishOnce)();
    }
  }, false);

  const { setActive } = frameCallback;
  useEffect(() => {
    setActive(true);
    // The frame clock is the fast path; these make sure the app is reached and the overlay goes.
    const navigateDeadline = setTimeout(
      navigateOnce,
      WELCOME_GALAXY_ENTRY_HANDOFF_T * 1000 + ENTRY_DEADLINE_SLACK_MS,
    );
    const finishDeadline = setTimeout(
      finishOnce,
      (isWelcomeGalaxyDive(destination.get())
        ? WELCOME_GALAXY_DIVE_MAX_DURATION
        : WELCOME_GALAXY_ENTRY_DURATION) *
        1000 +
        ENTRY_DEADLINE_SLACK_MS,
    );
    return () => {
      setActive(false);
      clearTimeout(navigateDeadline);
      clearTimeout(finishDeadline);
    };
  }, [destination, finishOnce, navigateOnce, setActive]);

  // No animated view styles here: the canvas fades itself (see `WelcomeGalaxyFrame.opacity`), so
  // the entry never needs a shadow-tree commit while React commits the app underneath.
  return (
    <View className="absolute inset-0" pointerEvents="none" testID="welcome-galaxy-entry">
      <View
        className="absolute top-0 left-0"
        style={{ height: viewport.height, width: viewport.width }}
      >
        {Canvas === null
          ? null
          : createElement(Canvas, {
              appearance,
              cache: canvasCache,
              entryColors,
              entryTime,
              launch,
              role: "entry",
              simTime,
              spinOffset,
              viewport,
            })}
      </View>
    </View>
  );
}
